import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { FaPlus, FaEdit, FaGripLines } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useQuery, useMutation } from 'react-apollo';
import { toast } from 'react-toastify';
import { displayError } from '../utilities';

import AddEditCategoryModal from './AddEditCategoryModal';
import DeleteCategory from './DeleteCategory';

import { GET_CATEGORIES, REORDER_CATEGORIES } from '../queries';

const SpecialtyCategoriesTab = () => {
  const { data, loading: queryLoading, refetch } = useQuery(GET_CATEGORIES);
  const [categoryList, setCategoryList] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const toggleModal = () => setModalOpen(!modalOpen);

  useEffect(() => {
    if (data && data.getCategories) {
      setCategoryList(data.getCategories);
    }
  }, [data]);

  const [reorderCategories] = useMutation(REORDER_CATEGORIES);

  const openAdd = () => {
    setIsEdit(false);
    setSelectedCategory(null);
    setModalOpen(true);
  };

  const openEdit = category => {
    setIsEdit(true);
    setSelectedCategory(category);
    setModalOpen(true);
  };

  const onDragEnd = async result => {
    if (!result.destination) return;

    const items = Array.from(categoryList);
    const [moved] = items.splice(result.source.index, 1);

    items.splice(result.destination.index, 0, moved);

    const updated = items.map((item, index) => ({
      ...item,
      displayOrder: index + 1
    }));

    setCategoryList(updated);

    try {
      const payload = updated.map(cat => ({
        id: cat.id,
        displayOrder: cat.displayOrder
      }));

      await reorderCategories({
        variables: { input: payload }
      });

      toast.success('Display order updated successfully', { autoClose: 3000 });
    } catch (err) {
      displayError(err);
    }
  };

  if (queryLoading) return <div>Loading...</div>;

  return (
    <div className="mapping-wrapper">
      <div className="mapping-header-row">
        <div className="mapping-table">
          <div className="category-header">
            <div className="cell header-cell">Display Order</div>
            <div className="cell header-cell">Category Name</div>
            <div className="cell header-cell">Icon</div>
            <div className="cell header-cell">Actions</div>
          </div>
        </div>

        <Button className="add-mapping-btn" onClick={openAdd}>
          <FaPlus className="add-icon" />
          Add New Category
        </Button>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="categories">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {categoryList.map((cat, index) => (
                <Draggable key={cat.id} draggableId={cat.id} index={index}>
                  {provided => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="category-row"
                    >
                      <div
                        className="cell display-order"
                        {...provided.dragHandleProps}
                      >
                        <FaGripLines className="drag-icon" />
                        {index + 1}
                      </div>

                      <div className="cell">{cat.name}</div>

                      <div className="cell icon-cell">
                        {cat.iconLink ? (
                          <img
                            src={cat.iconLink}
                            alt={cat.name}
                            className="category-icon"
                          />
                        ) : (
                          <div className="icon-placeholder" />
                        )}
                      </div>

                      <div className="cell actions">
                        <span className="edit" onClick={() => openEdit(cat)}>
                          <FaEdit />
                          Edit
                        </span>

                        <DeleteCategory category={cat} onSuccess={refetch} />
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <AddEditCategoryModal
        isOpen={modalOpen}
        toggle={toggleModal}
        isEdit={isEdit}
        category={selectedCategory}
        onSuccess={refetch}
      />
    </div>
  );
};

SpecialtyCategoriesTab.propTypes = {
  mappings: PropTypes.array
};

export default SpecialtyCategoriesTab;
